import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { PageHeader } from "@/components/patterns";
import { ErrorState } from "@/components/patterns/States";
import { FormSkeleton } from "@/components/ui/page-skeletons";

type Rule = { key: string; label: string; in_app: boolean; email: boolean; whatsapp: boolean; push: boolean; people_can_change: boolean; customised: boolean };
type Rules = { events: Rule[]; push_ready: boolean; whatsapp_ready: boolean };
type Channel = "in_app" | "push" | "email" | "whatsapp";

const CHANNELS: { key: Channel; label: string; say: string }[] = [
  { key: "in_app", label: "In app", say: "in the app" },
  { key: "push", label: "Push", say: "as a push" },
  { key: "email", label: "Email", say: "by email" },
  { key: "whatsapp", label: "WhatsApp", say: "on WhatsApp" },
];

/**
 * Settings → Notification rules: for each kind of notification, which channels everyone gets, and whether people may
 * choose for themselves. Kinds that people can't change are handled here; the rest show on each person's own page.
 */
export function NotificationRulesPage() {
  const { success, error } = useToast();
  const [rules, setRules] = useState<Rules | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      setFailed(false);
      setRules(unwrapObject<Rules>(await api.get("/api/Notification/Defaults")));
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const save = async (key: string, change: Partial<Record<Channel | "people_can_change", boolean>>) => {
    try {
      setRules(unwrapObject<Rules>(await api.put("/api/Notification/Defaults", { events: { [key]: change } })));
      success("Saved", "The rule is updated for everyone");
    } catch (err: any) {
      error("Could not save", err?.message || "Please try again");
    }
  };

  if (failed) return <ErrorState title="Couldn't load the notification rules" onRetry={load} />;
  if (!rules) return <FormSkeleton />;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <PageHeader title="Notification rules" />
      <p className="text-sm text-muted-foreground">
        Choose what everyone receives for each kind of notification. Turn on <strong className="font-semibold text-foreground">People can change</strong> for a kind to let each person
        choose for themselves; it then appears under their <Link to="/settings/preferences?tab=notifications" className="text-primary hover:underline">Preferences</Link>. Security alerts always arrive.
      </p>
      <section aria-label="Rules" className="rounded-[10px] border border-border bg-card px-4 py-3.5">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="py-1.5 text-left font-semibold">Kind</th>
                {CHANNELS.map((c) => (
                  <th key={c.key} className="w-[74px] text-center font-semibold">{c.label}</th>
                ))}
                <th className="w-[120px] text-center font-semibold">People can change</th>
              </tr>
            </thead>
            <tbody>
              {rules.events.map((r) => (
                <tr key={r.key} className="border-t border-border/60">
                  <td className="py-2.5 pr-2">
                    {r.label}
                    {r.customised && <span className="ml-2 rounded-full bg-accent px-1.5 py-px text-[10.5px] font-semibold text-primary">Changed</span>}
                  </td>
                  {CHANNELS.map((c) => (
                    <td key={c.key} className="text-center">
                      <input
                        type="checkbox"
                        checked={r[c.key]}
                        disabled={(c.key === "whatsapp" && !rules.whatsapp_ready && !r.whatsapp) || (c.key === "push" && !rules.push_ready && !r.push)}
                        onChange={(e) => save(r.key, { [c.key]: e.target.checked })}
                        aria-label={`${r.label} ${c.say}`}
                        className="size-4 accent-primary disabled:opacity-40"
                      />
                    </td>
                  ))}
                  <td className="text-center">
                    <input type="checkbox" checked={r.people_can_change} onChange={(e) => save(r.key, { people_can_change: e.target.checked })} aria-label={`${r.label}: people can change`} className="size-4 accent-primary" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!rules.push_ready && (
          <p className="pt-2 text-[11.5px] text-muted-foreground">
            Push notifications aren't set up yet. <Link to="/settings/push" className="text-primary hover:underline">Set them up</Link>
          </p>
        )}
        {!rules.whatsapp_ready && <p className="pt-1 text-[11.5px] text-muted-foreground">WhatsApp alerts need a connected business number.</p>}
        <p className="pt-2 text-[11.5px] text-muted-foreground">Email and WhatsApp go to the person's own address and mobile number. Each person's quiet hours still apply.</p>
      </section>
    </div>
  );
}
